import { get, set } from "@/utils/vuex";
import { merge, each } from "lodash";

let templateCache = {};

const getInitState = () => ({
	index: 1,
	device: null,
	meta: null,
	cls: null,
	steps: null,
	unpair: null,
	done: false,
	disableBack: true,
	disableNext: false,
	busy: false,
	popupData: null,
	templateCache: templateCache,
});

export default {
	namespaced: true,
	state: getInitState(),
	getters: {
		device: get("device"),
		meta: get("meta"),
		cls: get("cls"),
	},

	mutations: {
		setIndex: set("index"),
		setDevice: set("device"),
		setMeta: set("meta"),
		setCls: set("cls"),
		setSteps: set("steps"),
		setUnpair: set("unpair"),
		setDone: set("done"),
		setDisableBack: set("disableBack"),
		setDisableNext: set("disableNext"),
		setPopupData: set("popupData"),
		setBusy: set("busy"),

		resetState(state) {
			Object.assign(state, getInitState());
		},
	},

	actions: {
		reset({ state, commit }) {
			this._vm.$log(`Wizards: Reset state`);

			commit("resetState");
		},

		next({ state, commit }) {
			if (state.disableNext) {
				return;
			}
			if (state.index < state.steps.length) {
				commit("setDisableBack", false);
				commit("setIndex", state.index + 1);
			} else {
				commit("setDone", true);
			}
		},

		back({ state, commit }) {
			if (state.index > 0 && !state.disableBack) {
				commit("setIndex", state.index - 1);
				commit("setDisableNext", false);
			}

			if (state.index === 1) {
				commit("setDisableBack", true);
			}
		},

		async getTests({ state, commit, dispatch }, device) {
			const cls = device.cls || device.meta.cls;
			let template = await dispatch("getTemplate", cls);

			if (template && template.tests) {
				let tests = [];
				each(template.tests, t => {
					// construct a test object for each test
					let test = {};
					Object.assign(test, t);

					test.device = device;
					test.run = async () => {
						// Manual tests simply resolve and don't require a connection to the controller at all.
						if (test.type === "manual") {
							return Promise.resolve();
						}

						let k4 = await dispatch("controller/getModel", null, {
							root: true,
						});

						this._vm.$log(
							`Tests: Running test for ${test.device.name} ${test.device.path}`
						);
						switch (test.type) {
							case "variable":
								return dispatch("getVariableTest", {
									test,
									k4,
								});
							case "event":
								return dispatch("getEventTest", { test, k4 });
						}
					};

					tests.push(test);
				});
				return tests;
			} else {
				return [];
			}
		},

		getVariableTest({ state, commit, dispatch }, { test, k4 }) {
			// build a test around a variable change
			this._vm.$log(
				`Wizard: Variable test for ${test.device.path}/${test.path}`
			);

			return new Promise((resolve, reject) => {
				let node = k4.model.child(test.device.path);
				let variable = node.child(test.path);
				let current = variable.value();

				const handler = value => {
					
					this._vm.$log(
						`Wizard: Variable handler fired with current ${current} and value ${value}`
					);

					if (value !== current) {
						complete();
					}
				};

				const complete = error => {

					clearTimeout(timer);
					clearInterval(interval);

					variable.off("value", handler);
					this._vm.$log(
						`Wizard: Variable test complete: ${error || "success"}`
					);

					if (error) {
						reject(error);
					} else {
						resolve();
					}
				};

				variable.on("value", handler);

				let timer = setTimeout(() => {
					complete("Timeout waiting for test to complete. Please try again");
				}, 30000);

				let interval;

				if(test.poll)
				{
					interval = setInterval(()=>{
						node.child(test.poll).execute();
					}, 5000);
				}
			});
		},

		async getEventTest({ state, commit, dispatch }, { test, k4 }) {
			// build a test around an event fired
			this._vm.$log(`Tests: Event test for ${test.device.path}/${test.path}`);

			return new Promise((resolve, reject) => {
				let node = k4.model.child(test.device.path);
				let event = node.child(test.path);

				const handler = () => {
					
					this._vm.$log(`Wizard: Event handler fired`);
					complete();
				};

				const complete = error => {
					
					clearTimeout(timer);
					clearInterval(interval);
					
					event.off("fire", handler);
					this._vm.$log(`Wizard: Event test complete: ${error || "success"}`);

					if (error) {
						reject(error);
					} else {
						resolve();
					}
				};

				event.on("fire", handler);

				let timer = setTimeout(() => {
					complete("Wizard waiting for test to complete. Please try again");
				}, 30000);

				let interval;

				if(test.poll)
				{
					interval = setInterval(()=>{
						node.child(test.poll).execute();
					}, 5000);
				}
			});
		},

		async getTemplate({ state, commit }, cls) {
			let template;

			if (templateCache[cls]) {
				template = templateCache[cls];
			} else {
				// lookup to see if there is a config template to override the defaults
				try {
					let response = await fetch(`/wizards/${cls}.json`);
					template = await response.json();
					templateCache[cls] = template;
				} catch (e) {
					// no custom template, use the default only
					template = {};
				}
			}

			return template;
		},

		async load({ state, commit, dispatch }, { device, type, defaultConfig }) {
			const meta = device.description ? device.description.meta : device.meta;
			let cls = meta.cls;

			let template = await dispatch("getTemplate", cls);

			let config = merge(defaultConfig, template);

			await commit("setDevice", device);
			await commit("setMeta", meta);
			await commit("setCls", cls);
			await commit("setSteps", config.steps);
			await commit("setUnpair", config.unpair);
		},

		showPopup({ commit }, { text, button, title }) {
			console.log(text, button, title);

			return new Promise((resolve, reject) => {
				commit("setPopupData", {
					title,
					text,
					button,
					resolve,
					reject,
				});
			});
		},

		async hidePopup({ state, commit }) {
			commit("setPopupData", null);
		},
	},
};
